import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { exportReportsBetweenDatesToExcel, exportPlannedVisitsToExcel } from "../../../api/reportsApi";

type ExportType = "reports" | "planned";

// "yyyy-mm-dd" w czasie lokalnym (toISOString przesuwał 1. dzień miesiąca na ostatni dzień poprzedniego)
const toLocalDateString = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fromLocalDateString = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

interface CalendarNavigationProps {
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  onAddReport?: () => void;
}

export const CalendarNavigation: React.FC<CalendarNavigationProps> = ({
  onPreviousMonth,
  onNextMonth,
  onToday,
  onAddReport,
}) => {
  const { t } = useTranslation();
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState<ExportType>("planned");
  const [startDate, setStartDate] = useState(
    toLocalDateString(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  );
  const [endDate, setEndDate] = useState(
    toLocalDateString(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0))
  );
  const [isExporting, setIsExporting] = useState(false);

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const start = fromLocalDateString(startDate);
      const end = fromLocalDateString(endDate);

      const blob = exportType === "planned"
        ? await exportPlannedVisitsToExcel(start, end)
        : await exportReportsBetweenDatesToExcel(start, end);
      const prefix = exportType === "planned" ? "Planowane_wizyty" : "Raporty";

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${prefix}_${startDate}_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      setShowExportModal(false);
    } catch (error) {
      console.error("Nie udało się pobrać pliku", error);
      alert("Nie udało się wyeksportować danych do Excel");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <button
        onClick={onPreviousMonth}
        className="rounded-lg border border-[var(--sc-border)] p-2 hover:bg-[var(--sc-bg)] transition-colors"
        title={t("previousMonth")}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <button
        onClick={onToday}
        className="rounded-full px-4 py-1.5 text-sm font-medium bg-[#F4A832] text-[#0F0F0F] hover:bg-[#e09620] transition-colors"
      >
        {t("today")}
      </button>
      
      <button
        onClick={onNextMonth}
        className="rounded-lg border border-[var(--sc-border)] p-2 hover:bg-[var(--sc-bg)] transition-colors"
        title={t("nextMonth")}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      
      {/* Add Report Button */}
      {onAddReport && (
        <button
          onClick={onAddReport}
          className="flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium bg-[#F4A832] text-[#0F0F0F] hover:bg-[#e09620] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('addCalendarItem')}
        </button>
      )}
      
      {/* Export to Excel Button */}
      <button
        onClick={() => setShowExportModal(true)}
        className="flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium bg-[var(--sc-text)] text-[var(--sc-surface)] hover:opacity-80 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        {t('exportToExcel')}
      </button>
      
      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-[var(--sc-surface)] rounded-[12px] p-6 w-96 shadow-xl border border-[var(--sc-border)]">
            <h2 className="text-[15px] font-semibold text-[var(--sc-text)] mb-4">{t('exportToExcel')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-1">
                  {t('exportType')}
                </label>
                <div className="flex gap-2">
                  {([
                    ["planned", t('exportPlannedVisits')],
                    ["reports", t('exportReports')],
                  ] as [ExportType, string][]).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setExportType(value)}
                      disabled={isExporting}
                      className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
                        exportType === value
                          ? "bg-[#F4A832] border-[#F4A832] text-[#0F0F0F]"
                          : "border-[var(--sc-border)] text-[var(--sc-muted)] hover:bg-[var(--sc-bg)]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-1">
                  {t('startDate')}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-1">
                  {t('endDate')}
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
                className="rounded-full px-4 py-1.5 text-sm font-medium bg-[var(--sc-bg)] text-[var(--sc-muted)] hover:bg-[var(--sc-border)] disabled:opacity-50"
              >
                {t('close')}
              </button>
              <button
                onClick={handleExportToExcel}
                disabled={isExporting}
                className="rounded-full px-4 py-1.5 text-sm font-medium bg-[var(--sc-text)] text-[var(--sc-surface)] hover:opacity-80 disabled:opacity-50"
              >
                {isExporting ? t('exporting') : t('export')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
